import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import InstallPWA from "@/components/InstallPWA";
import { Bike, LogOut, Plus, Compass, Calendar, User as UserIcon } from "lucide-react";

const navItem = ({ isActive }) =>
  `px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${
    isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-900"
  }`;

const mobileNavItem = ({ isActive }) =>
  `flex flex-col items-center gap-0.5 flex-1 py-1.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${
    isActive ? "text-[#E11D48]" : "text-gray-600"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
        <Link to={user ? "/feed" : "/"} className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-sm bg-[#E11D48] flex items-center justify-center group-hover:bg-[#BE123C] transition-colors">
            <Bike className="w-5 h-5 text-gray-900" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl tracking-tighter uppercase">
            Bicy<span className="text-[#E11D48]">club</span>
          </span>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/feed" className={navItem} data-testid="nav-feed">
              <span className="flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Esplora</span>
            </NavLink>
            <NavLink to="/publish" className={navItem} data-testid="nav-publish">
              <span className="flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Pubblica</span>
            </NavLink>
            <NavLink to="/planned" className={navItem} data-testid="nav-planned">
              <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> In programma</span>
            </NavLink>
            <NavLink to="/profile" className={navItem} data-testid="nav-profile">
              <span className="flex items-center gap-2"><UserIcon className="w-3.5 h-3.5" /> Profilo</span>
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-2 md:gap-3">
          <InstallPWA />
          {user ? (
            <>
              <NotificationBell />
              <Link
                to="/profile"
                className="hidden sm:flex items-center gap-2 group"
                data-testid="nav-user-pill"
              >
                <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center group-hover:border-gray-400 transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-display font-black text-gray-500 uppercase tracking-tighter">
                      {((user.first_name?.[0] || "") + (user.last_name?.[0] || "")).toUpperCase() || "?"}
                    </span>
                  )}
                </div>
                <span className="text-xs uppercase tracking-wider font-bold text-gray-700 group-hover:text-gray-900" data-testid="nav-user-name">
                  {user.first_name}
                </span>
              </Link>
              <button
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="px-2.5 py-2 md:px-3 border border-gray-300 hover:border-gray-500 rounded-sm text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 md:gap-2"
              >
                <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Esci</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-xs uppercase tracking-[0.2em] font-bold text-gray-700 hover:text-gray-900" data-testid="nav-login">Accedi</Link>
              <Link to="/register" className="btn-primary px-4 py-2 rounded-sm text-xs" data-testid="nav-register">Iscriviti</Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile secondary nav (top, below logo bar) */}
      {user && (
        <nav className="md:hidden flex items-stretch border-t border-gray-200 bg-white/90 backdrop-blur" data-testid="mobile-nav">
          <NavLink to="/feed" className={mobileNavItem} data-testid="mobile-nav-feed">
            <Compass className="w-4 h-4" /> Esplora
          </NavLink>
          <NavLink to="/publish" className={mobileNavItem} data-testid="mobile-nav-publish">
            <Plus className="w-4 h-4" /> Pubblica
          </NavLink>
          <NavLink to="/planned" className={mobileNavItem} data-testid="mobile-nav-planned">
            <Calendar className="w-4 h-4" /> Programma
          </NavLink>
          <NavLink to="/profile" className={mobileNavItem} data-testid="mobile-nav-profile">
            <UserIcon className="w-4 h-4" /> Profilo
          </NavLink>
        </nav>
      )}
    </header>
  );
}
